import type { Key } from "react";
import { useState, useCallback } from "react";

export type UseSelectProperties<Value extends string> = {
  /**
   * The initial selected value.
   */
  initialSelectedKey: Value;

  /**
   * Whether the input is open.
   */
  isOpen?: boolean;

  /**
   * Callback triggered when the input is opened or closed.
   */
  onOpenChange?: (isOpen: boolean) => void;

  /**
   * Callback triggered when the selected value changes.
   */
  onSelectionChange?: (value: Value | null) => void;
};
export const useSelect = <Value extends string>({
  initialSelectedKey,
  isOpen: initialIsOpen = false,
  onOpenChange: externalHandleOpenChange,
  onSelectionChange,
}: UseSelectProperties<Value>) => {
  const [selectedKey, setSelectedKey] = useState<Value>(initialSelectedKey);
  const [internalIsOpen, setInternalIsOpen] = useState(initialIsOpen);

  const isOpen = externalHandleOpenChange ? initialIsOpen : internalIsOpen;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (externalHandleOpenChange) {
        externalHandleOpenChange(open);
      } else {
        setInternalIsOpen(open);
      }
    },
    [externalHandleOpenChange],
  );

  const handleSelectionChange = useCallback(
    (key: Key | null) => {
      setSelectedKey(key as Value);
      onSelectionChange?.(key as Value);
    },
    [onSelectionChange],
  );

  return {
    isOpen,
    onOpenChange: handleOpenChange,
    onSelectionChange: handleSelectionChange,
    value: selectedKey,
  };
};
